const MM_PER_INCH = 25.4;

function clamp(n, min, max) {
  return Math.max(min, Math.min(max, n));
}

/**
 * PanelBuilder-only connector footprint library.
 *
 * Units:
 * - All dimensions are in millimeters (mm) in the footprint definitions.
 * - Conversion to grid cells happens via the panel's gridSize (inches).
 *
 * Terminology:
 * - cutoutMm: the actual hole/opening needed in the panel
 * - outerMm: the connector's visible/plate envelope on the panel face
 * - keepoutMm: recommended clearance envelope for practical spacing
 */
const FAMILY_FOOTPRINTS = {
  // Neutrik "D-size" panel cutout family (XLR, Speakon, many 1/4" D jacks, RCA D, etc.)
  D_SIZE: {
    id: 'D_SIZE',
    label: 'Neutrik D-size',
    cutoutMm: { width: 24, height: 24, shape: 'circle' }, // Ø24mm
    outerMm: { width: 31, height: 31 }, // approx D-size face envelope
    keepoutMm: { width: 38, height: 38 } // practical spacing/clearance
  },

  // IEC C14 snap-in inlet (common cutout)
  IEC_C14_SNAPIN: {
    id: 'IEC_C14_SNAPIN',
    label: 'IEC C14 snap-in',
    cutoutMm: { width: 27.5, height: 20.0, shape: 'rect' },
    outerMm: { width: 34, height: 27 },
    keepoutMm: { width: 38, height: 30 }
  },

  // NEMA panel mount receptacle (varies widely; start conservative)
  NEMA_PANEL: {
    id: 'NEMA_PANEL',
    label: 'NEMA panel mount',
    cutoutMm: { width: 28, height: 28, shape: 'rect' },
    outerMm: { width: 32, height: 27 },
    keepoutMm: { width: 45, height: 35 }
  },

  // Neutrik powerCON TRUE1 chassis (approx until exact drawing numbers are added)
  TRUE1_CHASSIS: {
    id: 'TRUE1_CHASSIS',
    label: 'powerCON TRUE1 chassis',
    cutoutMm: { width: 30, height: 30, shape: 'circle' },
    outerMm: { width: 40, height: 40 },
    keepoutMm: { width: 50, height: 50 }
  }
};

function normalizeFamilyKey(familyOrType) {
  const raw = String(familyOrType || '').trim().toLowerCase();
  return raw.replace(/\s+/g, '');
}

/**
 * Maps current PanelBuilder families (from `compatibilityMatrix` connector.type)
 * to a baseline footprint family.
 */
export function getFootprintFamilyIdForConnector({ family, type } = {}) {
  const key = normalizeFamilyKey(family || type);

  if (!key) return 'D_SIZE';

  // D-size families
  if (key === 'xlr') return 'D_SIZE';
  if (key === 'speakon') return 'D_SIZE';
  if (key === 'rca') return 'D_SIZE';
  if (key === 'trs' || key === 'ts' || key === '1/4"' || key === '1/4') return 'D_SIZE';

  // Many PowerCON chassis are D-size; keep as D-size unless explicitly TRUE1
  if (key === 'powercon') return 'D_SIZE';

  // TRUE1 is larger
  if (key === 'true1') return 'TRUE1_CHASSIS';

  if (key === 'iec') return 'IEC_C14_SNAPIN';
  if (key === 'nema') return 'NEMA_PANEL';

  return 'D_SIZE';
}

export function getPanelConnectorFootprint({ family, type } = {}) {
  const familyId = getFootprintFamilyIdForConnector({ family, type });
  return FAMILY_FOOTPRINTS[familyId] || FAMILY_FOOTPRINTS.D_SIZE;
}

export function mmToInches(mm) {
  return (Number(mm) || 0) / MM_PER_INCH;
}

function mmBoxToGridCells(mmBox, gridSizeInches) {
  const wIn = mmToInches(mmBox?.width ?? 0);
  const hIn = mmToInches(mmBox?.height ?? 0);
  const cell = Number(gridSizeInches) || 0.125;

  return {
    widthCells: Math.max(1, Math.ceil(wIn / cell)),
    heightCells: Math.max(1, Math.ceil(hIn / cell))
  };
}

/**
 * Placement span (grid cells) used for collisions/snapping: keepout envelope (fallback: outer).
 */
export function getPlacementGridSpanForFootprint({ footprint, gridSizeInches }) {
  const keepout = footprint?.keepoutMm || footprint?.outerMm || { width: 31, height: 31 };
  return mmBoxToGridCells(keepout, gridSizeInches);
}

/**
 * Cutout span (grid cells) used for visuals: actual panel opening size.
 */
export function getCutoutGridSpanForFootprint({ footprint, gridSizeInches }) {
  const cutout = footprint?.cutoutMm || footprint?.outerMm || { width: 24, height: 24 };

  // UI consistency: render cutouts as SQUARE openings on the panel canvas.
  // Physically, many connectors are rectangular holes — we represent them using the
  // bounding square (max dimension) so every cutout reads as square in the builder.
  const sideMm = Math.max(Number(cutout.width) || 0, Number(cutout.height) || 0);
  const squareCutout = { width: sideMm, height: sideMm };

  return mmBoxToGridCells(squareCutout, gridSizeInches);
}

/**
 * Back-compat alias: historically this used keepout sizing (placement).
 */
export function getGridSpanForFootprint({ footprint, gridSizeInches }) {
  return getPlacementGridSpanForFootprint({ footprint, gridSizeInches });
}

/**
 * Returns ratios used to scale the connector image inside its footprint box.
 * NOTE: Prefer rendering images inside an explicit cutout box; these ratios are a legacy fallback.
 */
export function getImageScaleRatiosForFootprint({ footprint }) {
  const cutout = footprint?.cutoutMm || { width: 24, height: 24 };
  const outer = footprint?.outerMm || { width: 31, height: 31 };

  const w = outer.width > 0 ? cutout.width / outer.width : 0.75;
  const h = outer.height > 0 ? cutout.height / outer.height : 0.75;

  // Clamp so we never render microscopic or overflow the box.
  return {
    widthRatio: clamp(w, 0.55, 0.92),
    heightRatio: clamp(h, 0.55, 0.92)
  };
}

